import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Folder } from 'lucide-react';
import type { AutomationFolder } from '@/types/automation';

interface MoveFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: AutomationFolder[];
  currentFolderId?: string | null;
  onMove: (folderId: string | null) => void;
  automationName: string;
}

export function MoveFolderDialog({
  open,
  onOpenChange,
  folders,
  currentFolderId,
  onMove,
  automationName
}: MoveFolderDialogProps) {
  const handleMove = (folderId: string | null) => {
    onMove(folderId);
    // Don't close dialog here - let parent component handle it after operation completes
  };

  // Sort folders by display_order
  const sortedFolders = [...folders].sort((a, b) => a.display_order - b.display_order);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Move Automation</DialogTitle>
          <DialogDescription>
            Move "{automationName}" to a folder
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4 max-h-[400px] overflow-y-auto">
          {/* Uncategorized Option */}
          <button
            onClick={() => handleMove(null)}
            disabled={currentFolderId === null}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md border transition-all ${
              currentFolderId === null
                ? 'bg-muted border-border cursor-not-allowed opacity-50'
                : 'hover:bg-accent border-border cursor-pointer'
            }`}
          >
            <Folder className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1 text-left">
              <p className="font-medium text-sm">Uncategorized</p>
              <p className="text-xs text-muted-foreground">No folder</p>
            </div>
            {currentFolderId === null && (
              <span className="text-xs text-muted-foreground">Current</span>
            )}
          </button>

          {/* Folder Options */}
          {sortedFolders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => handleMove(folder.id)}
              disabled={currentFolderId === folder.id}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md border transition-all ${
                currentFolderId === folder.id
                  ? 'bg-muted border-border cursor-not-allowed opacity-50'
                  : 'hover:bg-accent border-border cursor-pointer'
              }`}
            >
              <Folder
                className="w-5 h-5"
                style={{ color: folder.color || undefined }}
              />
              <div className="flex-1 text-left">
                <p className="font-medium text-sm">{folder.name}</p>
                {folder.description && (
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {folder.description}
                  </p>
                )}
              </div>
              {currentFolderId === folder.id && (
                <span className="text-xs text-muted-foreground">Current</span>
              )}
            </button>
          ))}

          {/* Empty State */}
          {sortedFolders.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No folders created yet</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
